import React, { useEffect, useState, useCallback } from "react";
import { Card, Select, TextInput } from "@mantine/core";
import { HardwareModels, ProductRequest } from "types/productTypes";
import { useTranslation } from "react-i18next";
import { HARDWARE_MODELS } from "@constants/productConstants";
import { useForm } from "@mantine/form";
import { generateProductUuid, getProductHardwareLabel } from "@utils/productUtils";
import { getOrgIdsFromProductUuid } from "@utils/api-communication/RestApiCli";

// Define result enum for validation status
export enum ValidationResult {
  VALID = "valid",
  INVALID = "invalid",
  WARNING = "warning",
}

type DeviceCreationFormProps = {
  targetOrgId: string;
  onProductRequestChange: (productRequest: ProductRequest) => void;
  formRef?: React.MutableRefObject<{ validateDevice: () => Promise<ValidationResult> } | null>;
};

const DeviceCreationForm: React.FC<DeviceCreationFormProps> = ({
  onProductRequestChange,
  targetOrgId,
  formRef,
}) => {
  const { t } = useTranslation();

  const [isCheckingUuid, setIsCheckingUuid] = useState(false);

  const form = useForm({
    initialValues: {
      product: {
        hardwareModel: "",
        serialNo: "",
        alias: "",
      },
    },
  });

  const checkUuid = useCallback(async (): Promise<ValidationResult> => {
    const hardwareModel = form.values.product.hardwareModel;
    const serialNo = form.values.product.serialNo;

    if (!hardwareModel || !serialNo) {
      return ValidationResult.INVALID;
    }

    const uuid = generateProductUuid(hardwareModel, serialNo);
    if (!uuid) return ValidationResult.INVALID;

    if (isCheckingUuid) return ValidationResult.INVALID;
    setIsCheckingUuid(true);

    try {
      return await new Promise<ValidationResult>((resolve) => {
        getOrgIdsFromProductUuid(
          uuid,
          (response) => {
            const orgId: String[] = response?.data;
            if (orgId && orgId.length > 0 && !orgId.includes(targetOrgId)) {
              form.setFieldError(
                "product.serialNo",
                t("submissions.deviceCreationForm.warningOtherOrg"),
              );
              // Return warning to trigger confirmation flow
              resolve(ValidationResult.WARNING);
            } else if (orgId && orgId.includes(targetOrgId)) {
              form.setFieldError(
                "product.serialNo",
                t("submissions.deviceCreationForm.errorSameOrg"),
              );
              resolve(ValidationResult.INVALID);
            } else {
              form.clearFieldError("product.serialNo");
              resolve(ValidationResult.VALID);
            }
          },
          () => {
            form.clearFieldError("product.serialNo");
            resolve(ValidationResult.VALID);
          },
        );
      });
    } catch (error) {
      form.clearFieldError("product.serialNo");
      return ValidationResult.VALID;
    } finally {
      setIsCheckingUuid(false);
    }
  }, [form, targetOrgId, isCheckingUuid, t]);

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        validateDevice: checkUuid,
      };
    }
  }, [formRef, checkUuid]);

  useEffect(() => {
    const productRequest: ProductRequest = {
      ...form.values.product,
    };
    onProductRequestChange(productRequest);
  }, [form.values, onProductRequestChange]);

  return (
    <Card mt="sm" p="sm" style={{ background: "var(--oculera-surface-alt)" }}>
      <Select
        label={t("submissions.deviceCreationForm.hardwareModelLabel")}
        placeholder={t("submissions.deviceCreationForm.hardwareModelHint")}
        data={Object.entries(HARDWARE_MODELS).map(([key, value]) => ({
          value: HARDWARE_MODELS[key],
          label: getProductHardwareLabel(value as HardwareModels),
        }))}
        value={form.values.product.hardwareModel}
        onChange={(value) => value && form.setFieldValue("product.hardwareModel", value)}
        mb="sm"
      />
      <TextInput
        label={t("submissions.deviceCreationForm.serialNoLabel")}
        placeholder={t("submissions.deviceCreationForm.serialNoHint")}
        value={form.values.product.serialNo}
        onChange={(event) => form.setFieldValue("product.serialNo", event.currentTarget.value)}
        error={form.errors["product.serialNo"]}
        mb="sm"
        disabled={isCheckingUuid}
      />
      <TextInput
        label={t("submissions.deviceCreationForm.aliasLabel")}
        placeholder={t("submissions.deviceCreationForm.aliasHint")}
        value={form.values.product.alias}
        onChange={(event) => form.setFieldValue("product.alias", event.currentTarget.value)}
      />
    </Card>
  );
};

export default DeviceCreationForm;
